/**
 * The account's gender — ONE dictionary for every screen that reads or writes it (audit
 * 2026-10-19 #2/#3, BE-147).
 *
 * The API speaks `GenderEnum = woman | man | unisex | unknown` on `PUT /user/profile` and in
 * profile-data. Older accounts (the pre-release survey) still hold `female` / `male`, so READ
 * accepts both spellings; WRITE always sends the API's value. `unisex` / `unknown` / anything
 * else reads as "not known" — the screens then ask (Sizes, quizzes) or hide gendered parts (Heels).
 *
 * Not this dictionary: the sizing API's `SizingMeasureSilhouette` (`female | male | kids`, size
 * guides) and the people / gift-recipient genders (`girl`, `boy`).
 */

/** `GenderEnum` of the API. */
export type ApiProfileGender = 'woman' | 'man' | 'unisex' | 'unknown'

/** The two genders the profile screens act on; these are also the values the API stores. */
export type ProfileGender = 'woman' | 'man'

export const PROFILE_GENDERS: readonly ProfileGender[] = ['woman', 'man']

const READ: Readonly<Record<string, ProfileGender>> = {
  woman: 'woman',
  female: 'woman',
  women: 'woman',
  man: 'man',
  male: 'man',
  men: 'man',
}

/** Any stored spelling → `woman` / `man`, or `null` when the gender is not known. */
export function readProfileGender(raw: string | null | undefined): ProfileGender | null {
  return READ[raw?.trim().toLowerCase() ?? ''] ?? null
}

/** The value `PUT|PATCH /user/profile` accepts for a gender the user picked. */
export function toApiProfileGender(gender: ProfileGender): ProfileGender & ApiProfileGender {
  return gender
}
